import React from 'react';
import { LegalPage, Section } from '../components/LegalLayout';
import { Cookie, ShieldCheck, Zap, BarChart3, Fingerprint } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const CookiePolicy = () => {
  return (
    <>
      <SEO 
        title="Cookie Protocols | Digital Identity" 
        description="Official cookie policy of THE_ARCHIVE."
        url="/cookie-policy"
      />

      {/* Pure Black & White Background Sync */}
      <div className="min-h-screen bg-white dark:bg-[#050505] text-black dark:text-white transition-colors duration-300">
        <LegalPage title="Cookie_Protocol" lastUpdated="06 FEB 2026">
          
          {/* --- TOP INTEL CARDS --- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            <InfoCard icon={<ShieldCheck size={20}/>} title="Data_Encryption" desc="Secure Assets" />
            <InfoCard icon={<Zap size={20}/>} title="Session_Sync" desc="Zero Latency" />
            <InfoCard icon={<Fingerprint size={20}/>} title="Identity_Safe" desc="Anonymous" />
          </div>

          {/* --- CORE ARCHIVE BOX --- */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-transparent border-2 border-black dark:border-white/10 p-8 md:p-12 rounded-[2rem] mb-20 relative overflow-hidden"
          >
            <div className="flex items-center gap-6 mb-8">
              <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center text-black">
                <Cookie size={28}/>
              </div>
              <div>
                <h3 className="text-xl font-black uppercase italic tracking-tighter">System_Intelligence</h3>
                <p className="text-emerald-500 text-[9px] font-black uppercase tracking-[0.4em]">Essential Assets Only</p>
              </div>
            </div>

            <p className="text-sm font-medium leading-relaxed max-w-3xl opacity-80">
              At <span className="font-bold italic">THE_ARCHIVE</span>, we deploy cookies strictly as digital assets to maintain your <span className="text-emerald-500">Active Session</span> and <span className="text-emerald-500">Inventory</span>. No intrusive tracking, just pure performance.
            </p>
          </motion.div>

          {/* --- DATA SECTIONS --- */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionWrapper>
                <Section 
                  num="01" 
                  title="Essential_Nodes" 
                  text="Mandatory for core architecture: secure authentication and real-time inventory updates." 
                />
              </SectionWrapper>

              <SectionWrapper>
                <Section 
                  num="02" 
                  title="Analytics_Tracking" 
                  text="Telemetry data used only for server optimization and load balancing. Anonymous processing." 
                />
              </SectionWrapper>
            </div>

            <SectionWrapper isHighlight={true}>
              <div className="flex flex-col md:flex-row gap-10 items-center justify-between">
                <div className="flex-1">
                  <Section 
                    num="03" 
                    title="User_Override" 
                    text="Modify settings anytime to purge session data. Resets will clear your active cart." 
                  />
                </div>
                <button className="w-full md:w-auto px-10 py-5 bg-black dark:bg-white text-white dark:text-black font-black uppercase text-[10px] tracking-[0.3em] hover:bg-emerald-500 dark:hover:bg-emerald-500 hover:text-white transition-all">
                  Purge_Cookies
                </button>
              </div>
            </SectionWrapper>
          </div>
        </LegalPage>
      </div>
    </>
  );
};

// --- STYLED WRAPPERS (Pure Black/White) ---
const SectionWrapper = ({ children, isHighlight = false }) => (
  <div className={`p-10 border-2 transition-all duration-300 ${
    isHighlight 
    ? 'border-emerald-500/50 bg-emerald-500/5' 
    : 'border-black/10 dark:border-white/5 bg-transparent'
  } rounded-[2rem]`}>
    {children}
  </div>
);

// --- HELPER INFO CARD ---
const InfoCard = ({ icon, title, desc }) => (
  <div className="p-8 border-2 border-black/10 dark:border-white/5 flex flex-col items-center text-center space-y-4 hover:border-emerald-500 transition-all group rounded-[2rem]">
    <div className="text-black dark:text-white group-hover:text-emerald-500 transition-colors">
      {icon}
    </div>
    <div className="space-y-1">
      <p className="text-[11px] font-black uppercase tracking-[0.3em]">{title}</p>
      <p className="text-[9px] text-emerald-500 uppercase font-black italic tracking-widest">{desc}</p>
    </div>
  </div>
);

export default CookiePolicy;